import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";

// 293: změna vlastního přihlašovacího e-mailu. Kód přijde na NOVOU adresu a e-mail se přepne
// až po jeho zadání — překlep tak nikoho nezamkne mimo účet. Stará adresa dostane upozornění.
export function EmailChangeDialog({ open, onOpenChange, currentEmail, onDone }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const codeRef = useRef(null);

  useEffect(() => { if (open) { setStep("email"); setEmail(""); setCode(""); setError(""); } }, [open]);
  useEffect(() => { if (step === "code") setTimeout(() => codeRef.current?.focus(), 50); }, [step]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/email-change/request", { email });
      setEmail(data.email); setCode(""); setStep("code");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/email-change/confirm", { code });
      toast.success(`E-mail změněn na ${data.user.email}. Na původní adresu jsme poslali upozornění.`);
      onDone?.();
      onOpenChange(false);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-md rounded-[20px]" data-testid="email-change-dialog">
        <DialogHeader>
          <DialogTitle>Změnit e-mail</DialogTitle>
          <DialogDescription>Tímto e-mailem se přihlašujete a chodí na něj upozornění.</DialogDescription>
        </DialogHeader>
        {step === "email" ? (
          <form onSubmit={send} className="space-y-4">
            <p className="text-sm text-muted-foreground">Teď: <b className="text-foreground break-all">{currentEmail}</b></p>
            <div>
              <Label htmlFor="new-email">Nový e-mail</Label>
              <Input id="new-email" type="email" inputMode="email" autoComplete="email" value={email} required autoFocus
                onChange={(e) => setEmail(e.target.value)} placeholder="jmeno@firma.cz"
                className="h-12 md:h-11 mt-1.5 text-base md:text-sm w-full min-w-0" data-testid="email-change-input" />
              <p className="text-[13px] text-muted-foreground mt-1.5">Na novou adresu pošleme kód. E-mail se změní až po jeho zadání.</p>
            </div>
            {error && <p className="text-destructive text-sm" data-testid="email-change-error">{error}</p>}
            <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="h-11 w-full md:w-auto">Zrušit</Button>
              <Button type="submit" disabled={busy || !email.trim()} className="h-11 w-full md:w-auto font-bold" data-testid="email-change-send">
                {busy ? "Posílám…" : "Poslat kód"}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={confirm} className="space-y-4">
            <div className="flex flex-col items-center text-center gap-2">
              <MailCheck size={32} className="text-brand" />
              <p className="text-sm text-muted-foreground">Poslali jsme 6místný kód na<br /><b className="text-foreground break-all">{email}</b></p>
            </div>
            <div>
              <Label htmlFor="email-code">Kód z e-mailu</Label>
              <Input id="email-code" ref={codeRef} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required
                className="h-12 mt-1.5 text-center text-2xl md:text-2xl tracking-[0.5em] font-bold w-full min-w-0" data-testid="email-change-code" />
            </div>
            {error && <p className="text-destructive text-sm" data-testid="email-change-error">{error}</p>}
            <Button type="submit" disabled={busy || code.length < 6} className="h-12 w-full font-bold" data-testid="email-change-confirm">
              {busy ? "Ověřuji…" : "Potvrdit změnu"}
            </Button>
            <div className="flex justify-between gap-3 text-sm">
              <button type="button" onClick={() => { setError(""); setStep("email"); }} className="text-muted-foreground hover:text-foreground">Jiná adresa</button>
              <button type="button" onClick={() => send()} disabled={busy} className="text-brand font-semibold hover:underline" data-testid="email-change-resend">Poslat kód znovu</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
